import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import arCommon from '../locales/ar/common.json';
import arAuth from '../locales/ar/auth.json';
import arUsers from '../locales/ar/users.json';
import arDashboard from '../locales/ar/dashboard.json';
import arProducts from '../locales/ar/products.json';
import arTransactions from '../locales/ar/transactions.json';
import arPartners from '../locales/ar/partners.json';
import arOutlets from '../locales/ar/outlets.json';
import arInventory from '../locales/ar/inventory.json';
import arReports from '../locales/ar/reports.json';
import arSettings from '../locales/ar/settings.json';

import enCommon from '../locales/en/common.json';
import enAuth from '../locales/en/auth.json';
import enUsers from '../locales/en/users.json';
import enDashboard from '../locales/en/dashboard.json';
import enProducts from '../locales/en/products.json';
import enTransactions from '../locales/en/transactions.json';
import enPartners from '../locales/en/partners.json';
import enOutlets from '../locales/en/outlets.json';
import enInventory from '../locales/en/inventory.json';
import enReports from '../locales/en/reports.json';
import enSettings from '../locales/en/settings.json';

export const defaultNS = 'common';
export const fallbackLng = 'ar';
export const supportedLngs = ['ar', 'en'] as const;

export type SupportedLanguage = (typeof supportedLngs)[number];

export const resources = {
  ar: {
    common: arCommon,
    auth: arAuth,
    users: arUsers,
    dashboard: arDashboard,
    products: arProducts,
    transactions: arTransactions,
    partners: arPartners,
    outlets: arOutlets,
    inventory: arInventory,
    reports: arReports,
    settings: arSettings,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    users: enUsers,
    dashboard: enDashboard,
    products: enProducts,
    transactions: enTransactions,
    partners: enPartners,
    outlets: enOutlets,
    inventory: enInventory,
    reports: enReports,
    settings: enSettings,
  },
} as const;

export const languageConfig: Record<SupportedLanguage, { name: string; nativeName: string; dir: 'rtl' | 'ltr' }> = {
  ar: {
    name: 'Arabic',
    nativeName: 'العربية',
    dir: 'rtl',
  },
  en: {
    name: 'English',
    nativeName: 'English',
    dir: 'ltr',
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    defaultNS,
    fallbackLng,
    supportedLngs,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'nano-stock-language',
      caches: ['localStorage'],
    },
    react: {
      useSuspense: true,
    },
  });

export function getLanguageDirection(language: string): 'rtl' | 'ltr' {
  return language === 'ar' ? 'rtl' : 'ltr';
}

export function setLanguage(language: SupportedLanguage): void {
  i18n.changeLanguage(language);
  const dir = getLanguageDirection(language);
  document.documentElement.dir = dir;
  document.documentElement.lang = language;
  localStorage.setItem('nano-stock-language', language);
}

export default i18n;
